import { useTranslation } from "react-i18next";
import {
    ShieldCheckIcon,
    SignalIcon,
    EnvelopeIcon,
    LockClosedIcon,
    BugAntIcon,
    EyeIcon,
} from "@heroicons/react/20/solid";
import { Heading, Subheading } from "src/components/base/heading";
import { Text } from "src/components/base/text";
import { Badge } from "src/components/base/badge";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "src/components/base/table";
import type { View, ViewContext } from "../types";
import type { UnifiedEvent } from "../data/events";
import { UNIFIED_EVENTS } from "../data/events";
import { StatCard, Card, RingChart, HBar, FeedEntry, CorrelationBadges, IncidentChain } from "../shared";

export function PentestsDashboard({
    onNavigate,
    onInvestigate,
    onNavigateDetail,
}: {
    onNavigate: (v: View) => void;
    onInvestigate: (e: UnifiedEvent) => void;
    onNavigateDetail: (ctx: ViewContext) => void;
}) {
    const { t } = useTranslation();
    const ptEvents = UNIFIED_EVENTS.filter((e) => e.system === "pentests");
    const sevColor = { critical: "red", high: "orange", medium: "yellow", low: "zinc" } as const;
    const statusMap: Record<string, "openStatus" | "inProgress" | "fixed" | "accepted"> = {
        "PT-001": "openStatus",
        "PT-003": "inProgress",
        "PT-004": "inProgress",
        "PT-005": "accepted",
        "PT-006": "openStatus",
    };
    const statusColor = { openStatus: "red", inProgress: "amber", fixed: "green", accepted: "zinc" } as const;
    const assetMap: Record<string, string> = {
        "PT-001": "api.example.com",
        "PT-003": "AD DC-01",
        "PT-004": "lb-prod-02",
        "PT-005": "switch-floor3",
        "PT-006": "printer-3f-east",
    };
    const dueMap: Record<string, string> = {
        "PT-001": "2026-03-20",
        "PT-003": "2026-03-25",
        "PT-004": "2026-03-22",
        "PT-005": "—",
        "PT-006": "2026-04-01",
    };

    return (
        <>
            <Heading>{t("pentests.title")}</Heading>
            <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
                <StatCard label={t("pentests.totalFindings")} value="24" />
                <StatCard label={t("pentests.criticalFindings")} value="3" />
                <StatCard label={t("pentests.highFindings")} value="7" />
                <StatCard label={t("pentests.remediated")} value="14" trend="58% remediated" trendDown />
            </div>
            <div className="mt-8 grid gap-6 lg:grid-cols-3">
                <Card>
                    <Subheading>Severity Breakdown</Subheading>
                    <div className="mt-4 flex justify-center">
                        <RingChart
                            label="24"
                            segments={[
                                { value: 3, color: "#1e3a5f", label: t("soc.critical") },
                                { value: 7, color: "#2563eb", label: t("soc.high") },
                                { value: 8, color: "#60a5fa", label: t("soc.medium") },
                                { value: 6, color: "#bfdbfe", label: t("soc.low") },
                            ]}
                        />
                    </div>
                </Card>
                <Card>
                    <Subheading>Remediation Progress</Subheading>
                    <div className="mt-4">
                        <HBar
                            items={[
                                {
                                    label: t("pentests.fixed"),
                                    value: 14,
                                    max: 24,
                                    color: "bg-blue-700 dark:bg-blue-300",
                                },
                                { label: t("pentests.inProgress"), value: 5, max: 24, color: "bg-blue-400" },
                                {
                                    label: t("pentests.openStatus"),
                                    value: 3,
                                    max: 24,
                                    color: "bg-blue-700 dark:bg-blue-400",
                                },
                                { label: t("pentests.accepted"), value: 2, max: 24, color: "bg-blue-400" },
                            ]}
                        />
                    </div>
                </Card>
                <Card>
                    <Subheading>Cross-System Feed</Subheading>
                    <div className="mt-2 divide-y divide-zinc-950/5 dark:divide-white/5">
                        <FeedEntry
                            time="14:23"
                            icon={ShieldCheckIcon}
                            color="bg-blue-700 dark:bg-blue-400"
                            linkedSystem="soc"
                            onNavigate={onNavigate}
                        >
                            PT-001 live exploit detected by SOC
                        </FeedEntry>
                        <FeedEntry
                            time="13:45"
                            icon={EnvelopeIcon}
                            color="bg-blue-400 dark:bg-blue-500"
                            linkedSystem="mail"
                            onNavigate={onNavigate}
                        >
                            PT-004 exploit in Mail Gateway payload
                        </FeedEntry>
                        <FeedEntry
                            time="12:30"
                            icon={SignalIcon}
                            color="bg-blue-500"
                            linkedSystem="ndr"
                            onNavigate={onNavigate}
                        >
                            PT-005 SNMP enum detected by NDR
                        </FeedEntry>
                        <FeedEntry
                            time="11:30"
                            icon={LockClosedIcon}
                            color="bg-blue-400 dark:bg-blue-500"
                            linkedSystem="ztna"
                            onNavigate={onNavigate}
                        >
                            PT-003 MFA — ZTNA tokens enforced
                        </FeedEntry>
                        <FeedEntry
                            time="09:15"
                            icon={EnvelopeIcon}
                            color="bg-blue-700 dark:bg-blue-400"
                            linkedSystem="phishing"
                            onNavigate={onNavigate}
                        >
                            PT-001 social eng. in phishing campaign
                        </FeedEntry>
                    </div>
                </Card>
            </div>
            <Card className="mt-6">
                <Subheading>Cross-System Incident Chains</Subheading>
                <div className="mt-4 space-y-3">
                    <div>
                        <Text className="mb-1 text-xs font-medium sm:text-xs">PT-001: SQLi → Live Exploitation</Text>
                        <IncidentChain
                            onNavigate={onNavigate}
                            steps={[
                                { system: "pentests", label: "SQLi" },
                                { system: "soc", label: "Live exploit" },
                                { system: "ndr", label: "Exfil blocked" },
                                { system: "phishing", label: "Social eng." },
                                { system: "mail", label: "Emails blocked" },
                            ]}
                        />
                    </div>
                    <div>
                        <Text className="mb-1 text-xs font-medium sm:text-xs">PT-004: CVE → Active Campaign</Text>
                        <IncidentChain
                            onNavigate={onNavigate}
                            steps={[
                                { system: "pentests", label: "CVE found" },
                                { system: "mail", label: "In payload" },
                                { system: "ndr", label: "C2 matched" },
                                { system: "soc", label: "Patch prio" },
                                { system: "ztna", label: "Isolated" },
                            ]}
                        />
                    </div>
                </div>
            </Card>
            <Subheading className="mt-10">{t("pentests.findings")}</Subheading>
            <Table className="mt-4" dense>
                <TableHead>
                    <TableRow>
                        <TableHeader>{t("pentests.id")}</TableHeader>
                        <TableHeader>{t("pentests.finding")}</TableHeader>
                        <TableHeader>{t("pentests.severity")}</TableHeader>
                        <TableHeader>{t("pentests.asset")}</TableHeader>
                        <TableHeader>{t("pentests.status")}</TableHeader>
                        <TableHeader>{t("pentests.due")}</TableHeader>
                        <TableHeader>Correlated</TableHeader>
                        <TableHeader />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {ptEvents.map((e) => (
                        <TableRow
                            key={e.id}
                            className="cursor-pointer"
                            onClick={() => onNavigateDetail({ view: "pentest-finding-detail", detailId: e.id })}
                        >
                            <TableCell className="font-mono text-zinc-500">{e.id}</TableCell>
                            <TableCell className="font-medium">{e.title}</TableCell>
                            <TableCell>
                                <Badge color={sevColor[e.severity]}>{t(`soc.${e.severity}`)}</Badge>
                            </TableCell>
                            <TableCell className="font-mono text-xs">{assetMap[e.id] ?? "—"}</TableCell>
                            <TableCell>
                                <Badge color={statusColor[statusMap[e.id] ?? "openStatus"]}>
                                    {t(`pentests.${statusMap[e.id] ?? "openStatus"}`)}
                                </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-zinc-500">{dueMap[e.id] ?? "—"}</TableCell>
                            <TableCell>
                                <CorrelationBadges systems={e.correlated} onNavigate={onNavigate} />
                            </TableCell>
                            <TableCell>
                                <button
                                    onClick={(ev) => {
                                        ev.stopPropagation();
                                        onInvestigate(e);
                                    }}
                                    className="text-zinc-400 hover:text-blue-600"
                                >
                                    <EyeIcon className="h-4 w-4" />
                                </button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </>
    );
}
